/* eslint-disable react/prop-types */
/* eslint-disable import/prefer-default-export */
import * as React from 'react'
import type { FormikProps } from 'formik'
import cx from 'classnames'
import type { AlertSettingsValues } from '../configurations'
import * as styles from '../../setup.steps.styl'
import Button from '@src/components/common/button'
import type { AlertSettingsState } from '../index'
import type { StepProps } from '../../models'
import type { User } from '@src/redux/reducers/auth'
import { SystemLogs } from './system.log'
import { SysLogsDict, isEmail } from '../../helpers'
import { isEqual } from '@src/utils/comparison.utils'
import { remove, isEmpty } from 'lodash'
import TextBox from '@src/components/common/text.box'
import { correctEmailFormat } from '@src/utils/accounts'
import { useEffect } from 'react'

export interface AlertSettingsFormActions {
  updateUser: (user: User, callback?: () => void) => void
}

export interface FormProps {
  formikProps: FormikProps<AlertSettingsValues>
  stepProps: StepProps
  state: AlertSettingsState
  alertActions: AlertSettingsFormActions
}

interface State {
  newEmailAddress?: string
  newSlackUri?: string
}

export const AlertSettings: React.FC<FormProps> = React.memo(
  (props: FormProps) => {
    const {
      formikProps: {
        values,
        setValues,
        handleBlur,
        handleSubmit,
        initialValues
      },
      stepProps,
      state: parentState
    } = props

    const [state, setState] = React.useState<State>({
      newEmailAddress: '',
      newSlackUri: ''
    })

    function handleChangeNewEmailAddress(value: React.ReactText) {
      setState(prev => ({ ...prev, newEmailAddress: String(value) }))
    }

    function handleChangeSlackUri(value: React.ReactText) {
      setState(prev => ({ ...prev, newSlackUri: String(value) }))
    }

    function onAddNewUser() {
      const value = state.newEmailAddress
      if (value) {
        if (typeof setValues === 'function') {
          const emails = [
            ...values.notification_email_id,
            correctEmailFormat(value)
          ]
          setValues({ ...values, notification_email_id: emails })
          setState(prev => ({ ...prev, newEmailAddress: '' }))
        }
      }
    }

    function onAddSlackUri() {
      const value = state.newSlackUri
      if (value) {
        if (typeof setValues === 'function') {
          const slackUris = [...values.slackChannelUrl, value]
          setValues({ ...values, slackChannelUrl: slackUris })
          setState(prev => ({ ...prev, newSlackUri: '' }))
        }
      }
    }

    function onRemoveSlackUri(slackUri: string) {
      const slackUris = [...values?.slackChannelUrl] || []
      remove(slackUris, url => url === slackUri)
      if (typeof setValues === 'function') {
        setValues({ ...values, slackChannelUrl: slackUris })
      }
    }

    function onRemoveUser(email: string) {
      const emails = [...values?.notification_email_id] || []
      remove(emails, value => value === email)
      if (typeof setValues === 'function') {
        setValues({ ...values, notification_email_id: emails })
      }
    }

    function handleSystemLogs({ logs }: { logs: SysLogsDict }) {
      if (typeof setValues === 'function') {
        setValues({ ...values, syslog_servers: logs })
      }
    }
    useEffect(() => {
      setState(prev => ({
        ...prev,
        newEmailAddress: '',
        newSlackUri: ''
      }))
    }, [props.stepProps.severity])

    function validationEmail(value?: string | number) {
      return {
        isValid: isEmpty(value) || isEmail(String(value)),
        messages: 'Invalid email address'
      }
    }

    const validateEmail = validationEmail(state.newEmailAddress)

    return (
      <form className={styles.alertSettingsForm} onSubmit={handleSubmit}>
        <div className={styles.notifications}>Notification Method</div>
        <div className={styles.emailWrapper}>
          <div className={styles.currentEmail}>
            {`Email ${
              isEmpty(stepProps?.user?.email)
                ? ''
                : `(${stepProps?.user?.email})`
            }`}
          </div>
        </div>
        <div className={styles.addNewUserSection}>
          <div className={styles.addUserText}>
            {/* <i className="material-icons" onClick={this.showInfoPanel}>
            info_outline
          </i> */}
          </div>
          <div>
            <div className={styles.fieldWrapper}>
              <TextBox
                className={styles.inputWrapper}
                type="text"
                name="newEmailAddress"
                placeholder="Enter email address..."
                value={state.newEmailAddress}
                onChange={handleChangeNewEmailAddress}
                validation={validationEmail}
                notValidClassName={styles.error}
                onBlur={handleBlur}
                onKeyPress={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    if (validateEmail.isValid) {
                      onAddNewUser()
                    }
                  }
                }}
              />
              <Button
                className={styles.addUserButton}
                role="add-email-button"
                disabled={!validateEmail.isValid}
                title=" + ADD"
                icon={null}
                style="primary"
                onClick={onAddNewUser}
              />
            </div>
            {!validateEmail.isValid && (
              <div className={styles.errorMessages}>
                {validateEmail.messages}
              </div>
            )}
          </div>
          {(values.notification_email_id || []).map((email, index) => (
            <div className={styles.fieldWrapper} key={index}>
              <TextBox<any>
                className={styles.inputWrapper}
                type="text"
                name={`email${index}`}
                isActive={false}
                value={email}
              />
              <span
                role={'remove-email-button'}
                className={cx('mark-app-close', styles.textButton)}
                onClick={onRemoveUser.bind(null, email)}
              />
            </div>
          ))}
        </div>
        <div className={styles.notifications}>Slack Webhook URL</div>
        <div className={styles.slackSection}>
          <div className={styles.fieldWrapper}>
            <TextBox
              className={styles.inputWrapper}
              name="newSlackUrl"
              placeholder="e.g. https://hooks.slack.com/services/SDKJHDSV..."
              onBlur={handleBlur}
              value={state.newSlackUri}
              onChange={handleChangeSlackUri}
            />
            <Button
              role="add-slack-uri-button"
              className={styles.addUserButton}
              title=" + ADD"
              icon={null}
              style="primary"
              onClick={onAddSlackUri}
            />
          </div>
          {(values.slackChannelUrl || []).map((uri, index) => (
            <div className={styles.fieldWrapper} key={index}>
              <TextBox<any>
                className={styles.inputWrapper}
                type="text"
                name={`slack${index}`}
                isActive={false}
                value={uri}
              />
              <span
                role={'remove-slack-uri-button'}
                className={cx('mark-app-close', styles.textButton)}
                onClick={onRemoveSlackUri.bind(null, uri)}
              />
            </div>
          ))}
        </div>
        <SystemLogs logs={values.syslog_servers} onChange={handleSystemLogs} />
        <div
          className={cx(styles.infoPanel, {
            [styles.visible]: parentState.infoPanelHidden
          })}
        >
          <div className={styles.panelHeader}>
            <i className="material-icons">info_outline</i>
            <div>Details</div>
            {/* <i className="material-icons" onClick={this.hideInfoPanel}>
            clear
          </i> */}
          </div>
          <p>Details information</p>
        </div>
        {stepProps.footerComponent
          ? React.createElement(stepProps.footerComponent, {
              ...stepProps,
              disabledNext: isEqual(initialValues, values)
            })
          : null}
      </form>
    )
  }
)
